/**
 * Board and dungeon graph — GDD §12.
 *
 * Rooms form a directed graph, not a grid. A dice roll spends movement points
 * across edges, so a 6 can mean "three rooms down the safe corridor" or "one
 * room through the collapsed passage", which is what makes the number a decision
 * rather than a distance.
 */

import type { RoomId } from './types.js';
import type { Rng } from './rng.js';

export type RoomKind =
  | 'combat'
  | 'npc'
  | 'treasure'
  | 'trap'
  | 'shop'
  | 'puzzle'
  | 'event'
  | 'secret'
  | 'boss'
  | 'entrance';

export interface RoomEdge {
  readonly to: RoomId;
  /** Movement points required. Collapsed or guarded passages cost more. */
  readonly cost: number;
  /** Hidden edges need discovery before they appear (Fog of Fate, GDD §12). */
  readonly hidden?: boolean;
  /** World flag that must be set for this edge to be usable. */
  readonly requires?: string;
  readonly label?: string;
}

/** Grid position on the printed board. See content schema `boardPosSchema`. */
export interface BoardPos {
  readonly x: number;
  readonly y: number;
}

export interface RoomDef {
  readonly id: RoomId;
  readonly pos: BoardPos;
  readonly kind: RoomKind;
  readonly name: string;
  readonly description: string;
  readonly edges: readonly RoomEdge[];
  readonly encounterId?: string;
  readonly monsterIds?: readonly string[];
  readonly npcIds?: readonly string[];
  readonly secretId?: string;
  readonly assetId?: string;
  readonly musicId?: string;
  /**
   * 쉴 수 있는 방(GDD §12-1). 글만 콘텐츠에 있고 회복 수치는 엔진(`REST_HEAL_RATIO`)에 있다.
   * `nightmare` · `deep` 이 없으면 `text` 를 쓴다.
   */
  readonly rest?: { readonly text: string; readonly nightmare?: string; readonly deep?: string };
  /**
   * GDD §12: the dungeon adapts to habits. If the player always smashes doors,
   * doors thin out and traps multiply. Expressed as data so GOD-4 can author it.
   */
  readonly adaptiveRules?: readonly {
    readonly whenCounterAtLeast: readonly [string, number];
    readonly effect: 'addTrap' | 'sealDoor' | 'addGuard' | 'revealSecret';
  }[];
}

export interface DungeonDef {
  readonly id: string;
  readonly name: string;
  readonly entrance: RoomId;
  readonly bossRoom: RoomId;
  readonly rooms: readonly RoomDef[];
  readonly regionId: string;
}

export class Dungeon {
  private readonly byId: Map<RoomId, RoomDef>;

  constructor(readonly def: DungeonDef) {
    this.byId = new Map(def.rooms.map((r) => [r.id, r]));
    this.validate();
  }

  /** Fail loudly at construction: a dangling edge is a content bug, not a runtime surprise. */
  private validate(): void {
    const problems: string[] = [];
    if (!this.byId.has(this.def.entrance)) problems.push(`entrance '${this.def.entrance}' missing`);
    if (!this.byId.has(this.def.bossRoom)) problems.push(`bossRoom '${this.def.bossRoom}' missing`);
    for (const room of this.def.rooms) {
      for (const edge of room.edges) {
        if (!this.byId.has(edge.to)) {
          problems.push(`room '${room.id}' -> unknown room '${edge.to}'`);
        }
      }
    }
    if (problems.length > 0) {
      throw new Error(`Dungeon '${this.def.id}' is invalid:\n  - ${problems.join('\n  - ')}`);
    }
  }

  room(id: RoomId): RoomDef {
    const r = this.byId.get(id);
    if (!r) throw new Error(`Unknown room '${id}' in dungeon '${this.def.id}'`);
    return r;
  }

  has(id: RoomId): boolean {
    return this.byId.has(id);
  }

  get rooms(): readonly RoomDef[] {
    return this.def.rooms;
  }

  /** Edges the player can currently see and use. */
  availableEdges(
    from: RoomId,
    ctx: { flags: ReadonlySet<string>; discoveredSecrets: ReadonlySet<string> },
  ): RoomEdge[] {
    return this.room(from).edges.filter((e) => {
      if (e.requires && !ctx.flags.has(e.requires)) return false;
      if (e.hidden) {
        const target = this.byId.get(e.to);
        const secret = target?.secretId;
        return secret !== undefined && ctx.discoveredSecrets.has(secret);
      }
      return true;
    });
  }

  /**
   * Every room reachable with exactly `points` movement (a die face), plus the
   * cheaper stops along the way, so the UI can offer real alternatives.
   *
   * Returns entries sorted by cost so "spend it all" and "stop short" are both
   * visible choices.
   */
  reachable(
    from: RoomId,
    points: number,
    ctx: { flags: ReadonlySet<string>; discoveredSecrets: ReadonlySet<string> },
  ): { room: RoomId; cost: number; path: RoomId[] }[] {
    const best = new Map<RoomId, { cost: number; path: RoomId[] }>();
    const queue: { room: RoomId; cost: number; path: RoomId[] }[] = [
      { room: from, cost: 0, path: [from] },
    ];

    while (queue.length > 0) {
      const cur = queue.shift()!;
      for (const edge of this.availableEdges(cur.room, ctx)) {
        const cost = cur.cost + edge.cost;
        if (cost > points) continue;
        const prev = best.get(edge.to);
        if (prev && prev.cost <= cost) continue;
        const entry = { cost, path: [...cur.path, edge.to] };
        best.set(edge.to, entry);
        queue.push({ room: edge.to, ...entry });
      }
    }

    best.delete(from);
    return [...best.entries()]
      .map(([room, v]) => ({ room, cost: v.cost, path: v.path }))
      .sort((a, b) => a.cost - b.cost || a.room.localeCompare(b.room));
  }

  /** Shortest-hop path, for hints and for the boss beeline. */
  pathTo(
    from: RoomId,
    to: RoomId,
    ctx: { flags: ReadonlySet<string>; discoveredSecrets: ReadonlySet<string> },
  ): RoomId[] | null {
    const seen = new Set<RoomId>([from]);
    const queue: RoomId[][] = [[from]];
    while (queue.length > 0) {
      const path = queue.shift()!;
      const tail = path[path.length - 1]!;
      if (tail === to) return path;
      for (const edge of this.availableEdges(tail, ctx)) {
        if (seen.has(edge.to)) continue;
        seen.add(edge.to);
        queue.push([...path, edge.to]);
      }
    }
    return null;
  }

  /** Rooms the player has not yet cleared — used for "unfinished business" prompts. */
  unclearedRooms(cleared: ReadonlySet<RoomId>): RoomDef[] {
    return this.def.rooms.filter((r) => r.kind !== 'entrance' && !cleared.has(r.id));
  }
}

/** One drawable connection between two rooms, de-duplicated across directions. */
export interface BoardEdge {
  readonly from: RoomId;
  readonly to: RoomId;
  readonly cost: number;
  readonly hidden: boolean;
  readonly requires?: string;
}

/**
 * Every connection in the dungeon, each pair listed once. The map draws corridors
 * from this; movement still goes through `reachable`, which respects direction,
 * gating and cost.
 */
export function boardEdges(dungeon: Dungeon): BoardEdge[] {
  const seen = new Set<string>();
  const out: BoardEdge[] = [];
  for (const room of dungeon.rooms) {
    for (const edge of room.edges) {
      const key = [room.id, edge.to].sort().join('|');
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        from: room.id,
        to: edge.to,
        cost: edge.cost,
        hidden: edge.hidden === true,
        ...(edge.requires ? { requires: edge.requires } : {}),
      });
    }
  }
  return out;
}

/**
 * Fog of Fate (GDD §12): rooms beyond `radius` hops are unknown. Returns the set
 * of rooms the map should render.
 */
export function visibleRooms(
  dungeon: Dungeon,
  from: RoomId,
  radius: number,
  ctx: { flags: ReadonlySet<string>; discoveredSecrets: ReadonlySet<string> },
): Set<RoomId> {
  const visible = new Set<RoomId>([from]);
  let frontier: RoomId[] = [from];
  for (let hop = 0; hop < radius; hop++) {
    const next: RoomId[] = [];
    for (const room of frontier) {
      for (const edge of dungeon.availableEdges(room, ctx)) {
        if (!visible.has(edge.to)) {
          visible.add(edge.to);
          next.push(edge.to);
        }
      }
    }
    frontier = next;
  }
  return visible;
}

/**
 * Apply adaptive dungeon rules to a room (GDD §12). Returns modifications rather
 * than mutating the content definition, which stays immutable.
 */
export function adaptRoom(
  room: RoomDef,
  counters: Readonly<Record<string, number>>,
): { addTraps: number; sealedDoors: number; extraGuards: number; revealedSecret: boolean } {
  const out = { addTraps: 0, sealedDoors: 0, extraGuards: 0, revealedSecret: false };
  for (const rule of room.adaptiveRules ?? []) {
    const [key, threshold] = rule.whenCounterAtLeast;
    if ((counters[key] ?? 0) < threshold) continue;
    switch (rule.effect) {
      case 'addTrap':
        out.addTraps++;
        break;
      case 'sealDoor':
        out.sealedDoors++;
        break;
      case 'addGuard':
        out.extraGuards++;
        break;
      case 'revealSecret':
        out.revealedSecret = true;
        break;
    }
  }
  return out;
}

/** Pick a room to place a wandering NPC or a delayed consequence in. */
export function pickRoomForKind(dungeon: Dungeon, kind: RoomKind, rng: Rng): RoomDef | null {
  const candidates = dungeon.rooms.filter((r) => r.kind === kind);
  return candidates.length > 0 ? rng.pick(candidates) : null;
}
